import { listHelpers, ListKeymap } from '@tiptap/extension-list-keymap';
import type { Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import { isNodeActive } from '@tiptap/react';

const isAtListItemStart = (editor: Editor) => {
  const { selection } = editor.state;
  const isListItem = editor.isActive('listItem');

  const { $from } = selection;
  const listNode = $from.node(-1);

  return isListItem && listNode.firstChild === $from.node() && $from.parentOffset === 0;
};

const handleBackspace = (editor: Editor, name: string, parentListTypes: string[]) => {
  // this is required to still handle the undo handling
  if (editor.commands.undoInputRule()) {
    return true;
  }

  // if the selection is not collapsed
  // we can rely on the default backspace behavior
  if (editor.state.selection.from !== editor.state.selection.to) {
    return false;
  }

  // if the current item is NOT inside a list item &
  // the previous item is a list (orderedList or bulletList)
  // move the cursor into the list and delete the current item
  if (!isNodeActive(editor.state, name) && listHelpers.hasListBefore(editor.state, name, parentListTypes)) {
    const { $anchor } = editor.state.selection;

    const $listPos = editor.state.doc.resolve($anchor.before() - 1);

    const listDescendants: Array<{ node: Node; pos: number }> = [];

    $listPos.node().descendants((node, pos) => {
      if (node.type.name === name) {
        listDescendants.push({ node, pos });
      }
    });

    const lastItem = listDescendants.at(-1);

    if (!lastItem) {
      return false;
    }

    const $lastItemPos = editor.state.doc.resolve($listPos.start() + lastItem.pos + 1);

    return editor
      .chain()
      .cut({ from: $anchor.start() - 1, to: $anchor.end() + 1 }, $lastItemPos.end())
      .joinForward()
      .run();
  }

  // if the cursor is not inside the current node type
  // do nothing and proceed
  if (!isNodeActive(editor.state, name)) {
    return false;
  }

  // if the cursor is not at the start of a node
  // do nothing and proceed
  if (!isAtListItemStart(editor)) {
    return false;
  }

  return editor.chain().liftListItem(name).run();
};

export const CustomKeymap = ListKeymap.extend({
  addProseMirrorPlugins() {
    const parentPlugins = this.parent?.() ?? [];

    // Stop Tab/Shift-Tab from propagating to Outlook's handlers
    const tabTrapPlugin = new Plugin({
      key: new PluginKey('tabTrap'),
      props: {
        handleDOMEvents: {
          keydown(_view, event) {
            if (event.key === 'Tab') {
              event.stopImmediatePropagation();
            }
            // Let ProseMirror continue processing via addKeyboardShortcuts
            return false;
          },
        },
      },
    });

    return [...parentPlugins, tabTrapPlugin];
  },

  addKeyboardShortcuts() {
    const parentShortcuts = this.parent?.() ?? {};
    return {
      ...parentShortcuts,
      Backspace: ({ editor }) => {
        let handled = false;

        this.options.listTypes.forEach(({ itemName, wrapperNames }) => {
          if (editor.state.schema.nodes[itemName] === undefined) {
            return;
          }

          if (handleBackspace(editor, itemName, wrapperNames)) {
            handled = true;
          }
        });

        return handled;
      },
      Tab: ({ editor }) => {
        if (editor.isActive('taskItem')) {
          editor.chain().sinkListItem('taskItem').run();
        } else if (editor.isActive('listItem')) {
          editor.chain().sinkListItem('listItem').run();
        } else {
          editor.commands.insertContent('\t');
        }
        // Prevent default tab key processing
        return true;
      },
      'Shift-Tab': ({ editor }) => {
        if (editor.isActive('taskItem')) {
          editor.chain().liftListItem('taskItem').run();
        } else if (editor.isActive('listItem')) {
          editor.chain().liftListItem('listItem').run();
        } else {
          // Remove a preceding tab character if present (Word-like behavior)
          const { from } = editor.state.selection;
          const textBefore = editor.state.doc.textBetween(Math.max(0, from - 1), from);
          if (textBefore === '\t') {
            editor.chain().deleteRange({ from: from - 1, to: from }).run();
          }
        }
        return true;
      },      
    };
  },
});
